import { NgClass } from '@angular/common'
import { Component, Input } from '@angular/core'
import { Icon } from '@app/components/icon/icon'
import {
  formatDateTimeThai,
  formatRelativeTimeThai,
} from '@/app/utils/date-thai'
import {
  NotificationItem,
  notificationCategoryOf,
} from '@/app/types/notification'

@Component({
  selector: 'app-notification-card',
  imports: [Icon, NgClass],
  template: `
    <span class="dwh-noti-card__icon" [ngClass]="iconWrapClass" aria-hidden="true">
      <app-icon [icon]="iconName" class="fs-4"></app-icon>
    </span>

    <span class="dwh-noti-card__body">
      <span class="dwh-noti-card__status">
        @if (!item.is_read) {
          <span class="badge rounded-pill bg-primary-subtle text-primary">ยังไม่อ่าน</span>
        } @else {
          <span class="badge rounded-pill dwh-noti-card__badge--read">อ่านแล้ว</span>
        }
      </span>

      <span class="dwh-noti-card__title">{{ item.title }}</span>

      @for (line of bodyLines; track $index) {
        <span class="dwh-noti-card__detail">{{ line }}</span>
      }

      <span class="dwh-noti-card__time">
        <app-icon icon="clock" class="dwh-noti-card__clock"></app-icon>
        <span>{{ createdAtLabel }}</span>
        @if (relativeLabel) {
          <span class="dwh-noti-card__relative">({{ relativeLabel }})</span>
        }
      </span>
    </span>
  `,
  styles: `
    :host {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      width: 100%;
    }

    .dwh-noti-card__icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
    }

    .dwh-noti-card__body {
      min-width: 0;
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .dwh-noti-card__status .badge {
      font-size: 0.65rem;
      font-weight: 600;
    }

    .dwh-noti-card__badge--read {
      background-color: #e2e8f0;
      color: #64748b;
    }

    .dwh-noti-card__title {
      font-size: 0.8125rem;
      font-weight: 600;
      line-height: 1.4;
      color: var(--bs-body-color);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .dwh-noti-card__detail {
      font-size: 0.75rem;
      line-height: 1.4;
      color: var(--bs-secondary-color);
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .dwh-noti-card__time {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.15rem 0.35rem;
      margin-top: 0.35rem;
      font-size: 0.6875rem;
      line-height: 1.3;
      color: var(--bs-secondary-color);
    }

    .dwh-noti-card__clock {
      font-size: 0.6875rem;
    }

    .dwh-noti-card__relative {
      opacity: 0.85;
    }
  `,
})
export class NotificationCard {
  @Input({ required: true }) item!: NotificationItem

  get category() {
    return notificationCategoryOf(this.item.notification_type)
  }

  get iconName(): string {
    switch (this.item.notification_type) {
      case 'PIPELINE_RUN_STARTED':
        return 'circle-play'
      case 'PIPELINE_RUN_COMPLETED':
        return 'circle-check'
      case 'PIPELINE_RUN_FAILED':
        return 'circle-x'
      case 'OAUTH_SECRET_EXPIRING':
      case 'API_KEY_EXPIRING':
        return 'key'
      case 'USER_ROLE_ASSIGNED':
        return 'user-cog'
      case 'DATAMART_ADMIN_ASSIGNED':
        return 'user-plus'
      case 'FIELD_LEVEL_MISMATCH':
      case 'FILE_REMOVED_FROM_MART':
      case 'KEY_VALUE_UNSET':
        return 'triangle-alert'
      case 'REQUEST_SUBMITTED':
        return 'clipboard-list'
      case 'REQUEST_APPROVED':
        return 'file-check'
      case 'REQUEST_REJECTED':
      case 'REQUEST_CANCELLED':
        return 'file-x'
      default:
        return this.category === 'request' ? 'clipboard-list' : 'bell'
    }
  }

  get iconWrapClass(): string {
    if (!this.item.is_read) {
      return 'bg-primary-subtle text-primary'
    }
    return 'bg-transparent text-secondary'
  }

  get bodyLines(): string[] {
    return (this.item.body || '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .slice(0, 3)
  }

  get createdAtLabel(): string {
    return formatDateTimeThai(this.item.created_at)
  }

  get relativeLabel(): string {
    return formatRelativeTimeThai(this.item.created_at)
  }
}
